import { useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import { useTrackingDetailsContext } from '@/modules/pmt/components/TrackingDetails/context';
import { useMyParcelsContext, useShipmentDetailsContext } from '@/modules/pmt/context';
import {
  type IParcelEvent,
  type ITrackingProgressTracker,
  ShipmentSearchStatus,
  TrackingMilestone,
} from '@/modules/pmt/interfaces';
import { useMemo } from 'react';
// Styles
import {
  CardDetailContainer,
  CarrierSectionContainer,
  CarrierSectionHeading,
  EventCardContainer,
  TrackingEventCardContainer,
} from './TrackingEventCard.styles';
// Components
import { NotificationSection } from './components/NotificationSection';
import { TrackingCarrierSelector } from './components/TrackingCarrierSelector';
import { TrackingEventOrderConfirmed } from './components/TrackingEventCardHeader/components/TrackingEventOrderConfirmed';
import { TrackingEventPending } from './components/TrackingEventCardHeader/components/TrackingEventPending/TrackingEventPending';
import { TrackingEventCardHeaderMapping } from './components/TrackingEventCardHeader/constants';
import { TrackingProgressTracker } from './components/TrackingProgressTracker';
import { TrackingSummarySection } from './components/TrackingSummarySection';

interface ITrackingEventCard {
  event: IParcelEvent | null;
  eventHeaderState: ShipmentSearchStatus;
  progressTrackerState: ITrackingProgressTracker;
}
const Empty = () => <></>;

export const TrackingEventCard: React.FC<ITrackingEventCard> = (props) => {
  const { event, eventHeaderState, progressTrackerState } = props;
  const { currentShipment } = useShipmentDetailsContext();
  const shipmentDetails = currentShipment?.data;
  const { carrierByParcel, setCarrierByParcel } = useTrackingDetailsContext();
  const {
    notificationTranslation: { mobileText, desktopText },
  } = useMyParcelsContext();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);

  const isPendingShipment = useMemo(
    () =>
      eventHeaderState === ShipmentSearchStatus.Timeout ||
      eventHeaderState === ShipmentSearchStatus.ScrapingWithCarrier,
    [eventHeaderState],
  );

  const renderNotiData = (mobileData?: string, desktopData?: string) => {
    if (isDesktopPMT) {
      return desktopData || mobileData;
    }
    return mobileData || desktopData;
  };

  if (!shipmentDetails) return null;

  const TrackingEventCardHeader = TrackingEventCardHeaderMapping[eventHeaderState] || Empty;

  if (
    shipmentDetails.milestone === TrackingMilestone.Pending &&
    (shipmentDetails.isSMEShipment || shipmentDetails.isEmailSyncShipment)
  ) {
    return (
      <TrackingEventCardContainer>
        {shipmentDetails.isEmailSyncShipment && <TrackingSummarySection trackingDetails={shipmentDetails} />}
        <EventCardContainer>
          <CardDetailContainer>
            <TrackingEventPending trackingDetails={shipmentDetails} />
          </CardDetailContainer>
          <TrackingProgressTracker {...progressTrackerState} />
        </EventCardContainer>
      </TrackingEventCardContainer>
    );
  }

  // open shipment
  if (shipmentDetails.isOpenShipment) {
    return (
      <TrackingEventCardContainer>
        <EventCardContainer>
          <CardDetailContainer>
            <TrackingEventOrderConfirmed trackingDetails={shipmentDetails} />
          </CardDetailContainer>
          <TrackingProgressTracker {...progressTrackerState} />
        </EventCardContainer>
      </TrackingEventCardContainer>
    );
  }

  const carrierSection = isPendingShipment ? (
    <CarrierSectionContainer>
      <CarrierSectionHeading>{renderNotiData(mobileText, desktopText)}</CarrierSectionHeading>
      <TrackingCarrierSelector
        disabled={eventHeaderState === ShipmentSearchStatus.ScrapingWithCarrier}
        carrierByParcel={carrierByParcel}
        setCarrierByParcel={setCarrierByParcel}
      />
    </CarrierSectionContainer>
  ) : null;

  return (
    <>
      <TrackingEventCardContainer>
        {!isPendingShipment && <TrackingSummarySection trackingDetails={shipmentDetails} />}
        <EventCardContainer>
          <CardDetailContainer>
            <TrackingEventCardHeader event={event} trackingDetails={shipmentDetails} />
          </CardDetailContainer>
          <TrackingProgressTracker {...progressTrackerState} />
          {carrierSection}
          <NotificationSection />
        </EventCardContainer>
      </TrackingEventCardContainer>
    </>
  );
};
